import { useEffect, useState, type ReactNode } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
  Stack, TextField, Tooltip, Menu,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { updateAccount, resetAccountPassword, deleteAccount, type AccountSummary } from '../../lib/accounts-api';
import { extractApiError } from '../../lib/api-client';
import { CredentialsDialog } from '../platform/CredentialsDialog';
import { useT } from '../../i18n';

// Shared dialog chrome for the two Create dialogs (keeps pages small).
export function CreateAccountDialogShell({ title, open, onClose, busy, canSave, onSave, error, children }: {
  title: string; open: boolean; onClose: () => void; busy: boolean; canSave: boolean; onSave: () => void; error: string | null; children: ReactNode;
}) {
  const t = useT();
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {children}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button onClick={onSave} variant="contained" disabled={!canSave || busy}>{busy ? t('acc.creating') : t('acc.create')}</Button>
      </DialogActions>
    </Dialog>
  );
}

// Row actions: a kebab menu → Edit / Reset password / [Remove from this cluster] / Delete.
export function AccountRowActions({ account, onChanged, onRemoveFromCluster }: {
  account: AccountSummary; onChanged: () => void | Promise<void>; onRemoveFromCluster?: () => void | Promise<void>;
}) {
  const t = useT();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [credits, setCredits] = useState<{ email: string; password: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function doReset() {
    setAnchor(null); setBusy(true);
    try { const res = await resetAccountPassword(account.id); setCredits({ email: account.email, password: res.temp_password }); }
    catch (e) { setError(extractApiError(e).message); } finally { setBusy(false); }
  }

  return (
    <>
      <Tooltip title="Actions"><IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}><MoreVertIcon fontSize="small" /></IconButton></Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => { setAnchor(null); setEditing(true); }}>{t('common.edit')}</MenuItem>
        <MenuItem onClick={doReset} disabled={busy}>{t('acc.resetPassword')}</MenuItem>
        {onRemoveFromCluster ? <MenuItem onClick={() => { setAnchor(null); setRemoving(true); }}>{t('acc.removeFromCluster')}</MenuItem> : null}
        <MenuItem onClick={() => { setAnchor(null); setDeleting(true); }} sx={{ color: 'error.main' }}>{t('common.delete')}</MenuItem>
      </Menu>
      {error ? <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1 }}>{error}</Alert> : null}

      <EditAccountDialog account={editing ? account : null} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); onChanged(); }} />

      {onRemoveFromCluster ? (
        <Dialog open={removing} onClose={() => !busy && setRemoving(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('acc.removeTitle', { name: account.full_name })}</DialogTitle>
          <DialogContent><Alert severity="warning">{t('acc.removeWarning')}</Alert></DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoving(false)} disabled={busy}>{t('common.cancel')}</Button>
            <Button color="error" variant="contained" disabled={busy} onClick={async () => {
              setBusy(true); try { await onRemoveFromCluster(); setRemoving(false); await onChanged(); } catch (e) { setError(extractApiError(e).message); } finally { setBusy(false); }
            }}>{busy ? t('acc.removing') : t('acc.remove')}</Button>
          </DialogActions>
        </Dialog>
      ) : null}

      <Dialog open={deleting} onClose={() => !busy && setDeleting(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('acc.deleteTitle', { name: account.full_name })}</DialogTitle>
        <DialogContent><Alert severity="warning">{t('acc.deleteWarning')}</Alert></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(false)} disabled={busy}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" disabled={busy} onClick={async () => {
            setBusy(true); try { await deleteAccount(account.id); setDeleting(false); await onChanged(); } catch (e) { setError(extractApiError(e).message); } finally { setBusy(false); }
          }}>{busy ? t('common.deleting') : t('common.delete')}</Button>
        </DialogActions>
      </Dialog>

      <CredentialsDialog credits={credits} onClose={() => setCredits(null)} />
    </>
  );
}

function EditAccountDialog({ account, onClose, onSaved }: { account: AccountSummary | null; onClose: () => void; onSaved: () => void }) {
  const t = useT();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'ACTIVE' | 'SUSPENDED'>('ACTIVE');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (account) { setFullName(account.full_name); setEmail(account.email); setStatus(account.status === 'SUSPENDED' ? 'SUSPENDED' : 'ACTIVE'); setError(null); }
  }, [account]);
  async function save() {
    if (!account) return;
    setBusy(true); setError(null);
    try { await updateAccount(account.id, { full_name: fullName.trim(), email: email.trim(), status }); onSaved(); }
    catch (e) { setError(extractApiError(e).message); } finally { setBusy(false); }
  }
  return (
    <Dialog open={Boolean(account)} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{t('acc.editTitle', { name: account?.full_name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField label={t('common.fullName')} value={fullName} onChange={(e) => setFullName(e.target.value)} required fullWidth />
          <TextField label={t('common.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth />
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value as 'ACTIVE' | 'SUSPENDED')} fullWidth>
            <MenuItem value="ACTIVE">{t('acc.statusActive')}</MenuItem>
            <MenuItem value="SUSPENDED">{t('acc.statusSuspended')}</MenuItem>
          </TextField>
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button onClick={save} variant="contained" disabled={!fullName.trim() || !email.trim() || busy}>{busy ? t('common.saving') : t('common.save')}</Button>
      </DialogActions>
    </Dialog>
  );
}
